<script setup lang="ts">
import { computed, type Component } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import BaseIcon from './BaseIcon.vue';

interface Props {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  icon?: Component;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  type: 'button',
  fullWidth: false,
});

const buttonClass = computed(() => {
  const variants = {
    primary: 'bg-primary text-white hover:bg-primary-dark active:bg-primary-dark',
    secondary: 'bg-secondary text-white hover:bg-secondary-dark active:bg-secondary-dark',
    outline: 'bg-transparent border-2 border-primary text-primary hover:bg-primary-light active:bg-primary-light',
    ghost: 'bg-transparent text-primary hover:bg-bg-tertiary active:bg-bg-tertiary',
    danger: 'bg-error text-white hover:bg-error active:bg-error',
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const baseClasses = [
    'font-medium',
    'rounded-md',
    'transition-all',
    'duration-base',
    'ease-default',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-primary',
    'focus:ring-offset-2',
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
  ];

  const stateClasses = [
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'disabled:pointer-events-none',
  ];

  return [
    variants[props.variant],
    sizes[props.size],
    props.fullWidth ? 'w-full' : 'w-auto',
    ...baseClasses,
    ...stateClasses,
  ].filter(Boolean).join(' ');
});

const iconSize = {
  sm: 16,
  md: 20,
  lg: 24,
};
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClass"
    :aria-label="ariaLabel"
    :aria-busy="loading"
  >
    <BaseIcon v-if="loading" :icon="Loader2" :size="iconSize[size]" class="animate-spin" aria-hidden="true" />
    <BaseIcon v-else-if="icon" :icon="icon" :size="iconSize[size]" aria-hidden="true" />
    <slot />
  </button>
</template>

<style scoped>
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
